import React, { useState } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiPlay, FiEye, FiSearch, FiFilter } from 'react-icons/fi';

const MandiBhavVideos: React.FC = () => {
  // ...existing state

  // Delete video handler
  const handleDeleteVideo = async (id: string | number) => {
    if (!window.confirm('क्या आप वाकई इस वीडियो को हटाना चाहते हैं? (Are you sure you want to delete this video?)')) return;
    try {
      const res = await fetch(`https://mandilive.in/api/videos/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchVideos();
      } else {
        alert('वीडियो हटाने में असफल (Failed to delete video)');
      }
    } catch (err) {
      alert('Error deleting video');
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Videos state and loading
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    title: '',
    title_hindi: '',
    description: '',
    description_hindi: '',
    thumbnail: '', // allow manual thumbnail URL if needed
    date: '',
    likes: '',
    category: '',
    crop_id: '',
    status: 'published',
  });

  // Crop list state
  const [crops, setCrops] = useState<any[]>([]);

  // Fetch crops for dropdown
  React.useEffect(() => {
    fetch('https://mandilive.in/api/crops')
      .then(res => res.json())
      .then(data => setCrops(data.data || []));
  }, []);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Validation state
  const [errors, setErrors] = useState<any>({});

  // Helper: parse views (e.g. 8.5K -> 8500)
  function parseViews(val: string) {
    if (!val) return 0;
    if (/k$/i.test(val)) return Math.round(parseFloat(val) * 1000);
    if (/m$/i.test(val)) return Math.round(parseFloat(val) * 1000000);
    return parseInt(val.replace(/,/g, ''), 10) || 0;
  }

  // Helper: validate duration (m:ss or mm:ss)
  function validDuration(val: string) {
    return /^\d{1,2}:\d{2}$/.test(val);
  }

  function validateForm() {
    const newErrors: any = {};
    if (!addForm.title.trim()) newErrors.title = 'Title is required';
    if (!addForm.title_hindi.trim()) newErrors.title_hindi = 'Title (Hindi) is required';
    if (!addForm.description.trim()) newErrors.description = 'Description is required';
    if (!addForm.description_hindi.trim()) newErrors.description_hindi = 'Description (Hindi) is required';
    if (!addForm.likes || parseInt(addForm.likes) < 0) newErrors.likes = 'Likes is required (must be a number >= 0)';
    if (!addForm.category.trim()) newErrors.category = 'Category is required';
    // Crop is optional - only validate if a value is provided
    if (addForm.crop_id && addForm.crop_id !== 'other' && !crops.find(c => String(c.id) === String(addForm.crop_id))) {
      newErrors.crop_id = 'Invalid crop selection';
    }
    if (!videoFile) newErrors.video = 'Video file is required';
    else if (!videoFile.type.startsWith('video/')) newErrors.video = 'File must be a video';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // Helper to convert file path to full URL
  const getFileUrl = (filePath: string) => {
    if (!filePath) return '';
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath;
    const baseUrl = 'https://mandilive.in';
    let cleanPath = filePath.replace(/^[\\/]+/, '').replace(/\\/g, '/');
    if (!cleanPath.startsWith('uploads/')) cleanPath = `uploads/${cleanPath}`;
    return `${baseUrl}/${cleanPath}`;
  };

  // Fetch videos from backend
  const fetchVideos = async () => {
    setLoading(true);
    try {
      const timestamp = new Date().getTime();
      const res = await fetch(`https://mandilive.in/api/videos?_t=${timestamp}`, {
        cache: 'no-cache'
      });
      const data = await res.json();
      console.log('=== FETCH VIDEOS DEBUG ===');
      console.log('Raw API Response:', data);
      console.log('Response data array:', data.data);
      console.log('Array length:', data.data ? data.data.length : 0);
      
      const processed = (data.data || []).map((video: any) => {
        const processedVideo = {
          ...video,
          thumbnail: getFileUrl(video.thumbnail),
          uploadDate: video.date || video.uploadDate || '', // map 'date' to 'uploadDate' for UI
          views: video.views || '',
          title: video.title || '',
          category: video.category || '',
          status: video.status || 'published' // Add default status
        };
        console.log('Processing video ID', video.id, ':', processedVideo);
        return processedVideo;
      });
      
      console.log('Final processed videos:', processed);
      console.log('Total videos to set:', processed.length);
      setVideos(processed);
    } catch (err) {
      setVideos([]);
    }
    setLoading(false);
  };


  React.useEffect(() => {
    fetchVideos();
  }, []);

  // Handle form changes
  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  };

  // Handle video upload with progress tracking
  const handleAddVideo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('video', videoFile!);
    Object.entries(addForm).forEach(([key, value]) => {
      if (key === 'views') {
        formData.append(key, String(parseViews(value)));
      } else if (key === 'date' && value) {
        // Convert date to Hindi format for DB
        const hindiDate = new Date(value).toLocaleDateString('hi-IN', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
        formData.append(key, hindiDate);
      } else {
        formData.append(key, value);
      }
    });

    console.log('=== VIDEO UPLOAD DEBUG ===');
    console.log('FormData contents:');
    const entries = Array.from(formData.entries());
    entries.forEach(([key, value]) => {
      if (value instanceof File) {
        console.log(`${key}:`, `File - ${value.name} (${value.size} bytes, ${value.type})`);
      } else {
        console.log(`${key}:`, value);
      }
    });

    // Use XMLHttpRequest for progress tracking
    const xhr = new XMLHttpRequest();
    
    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(percentComplete);
        console.log(`Upload progress: ${percentComplete}%`);
      }
    };
    
    // Handle upload completion
    xhr.onload = () => {
      console.log('Upload response status:', xhr.status);
      console.log('Upload response text:', xhr.responseText);
      
      if (xhr.status >= 200 && xhr.status < 300) {
        let responseData;
        try {
          responseData = JSON.parse(xhr.responseText);
          console.log('Upload response JSON:', responseData);
          
          if (responseData.success === false) {
            console.error('Upload failed on server-side:', responseData.message);
            alert(`वीडियो अपलोड करने में त्रुटि: ${responseData.message} (Error uploading video: ${responseData.message})`);
            setUploading(false);
            setUploadProgress(0);
            return;
          }
          
          // Add a log to confirm the video ID was received
          if (responseData.video_id) {
            console.log('Video successfully saved in database with ID:', responseData.video_id);
          }
          
        } catch (e) {
          console.log('Response is not JSON, but upload was successful');
        }
        
        alert('वीडियो सफलतापूर्वक अपलोड हो गया! (Video uploaded successfully!)');
        setShowAddForm(false);
        setAddForm({
          title: '',
          title_hindi: '',
          description: '',
          description_hindi: '',
          thumbnail: '',
          date: '',
          likes: '',
          category: '',
          crop_id: '',
          status: 'published',
        });
        setVideoFile(null);
        setErrors({});
        setUploadProgress(0);
        
        // Ensure we refresh the videos list after successful upload
        setTimeout(() => {
          fetchVideos();
        }, 1000); // Small delay to ensure the backend has processed everything
      } else {
        console.error('Upload failed with status:', xhr.status);
        alert(`वीडियो अपलोड करने में असफल। सर्वर प्रतिक्रिया: ${xhr.responseText} (Failed to upload video. Server response: ${xhr.responseText})`);
      }
      
      setUploading(false);
    };
    
    // Handle upload errors
    xhr.onerror = () => {
      console.error('Upload error');
      alert('वीडियो अपलोड करने में त्रुटि हुई (Error uploading video)');
      setUploading(false);
      setUploadProgress(0);
    };
    
    // Handle upload abort
    xhr.onabort = () => {
      console.log('Upload aborted');
      setUploading(false);
      setUploadProgress(0);
    };
    
    // Start the upload
    xhr.open('POST', 'https://mandilive.in/api/videos');
    xhr.send(formData);
  };


  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         video.category.toLowerCase().includes(searchTerm.toLowerCase());
    // Since videos don't have status field in API response, treat all videos as 'published'
    const videoStatus = video.status || 'published';
    const matchesFilter = filterStatus === 'all' || videoStatus === filterStatus;
    
    console.log('Filtering video:', {
      id: video.id,
      title: video.title,
      searchTerm,
      matchesSearch,
      videoStatus,
      filterStatus,
      matchesFilter,
      finalResult: matchesSearch && matchesFilter
    });
    
    return matchesSearch && matchesFilter;
  });
  
  console.log('=== FILTERING RESULTS ===');
  console.log('Total videos before filtering:', videos.length);
  console.log('Search term:', searchTerm);
  console.log('Filter status:', filterStatus);
  console.log('Filtered videos count:', filteredVideos.length);
  console.log('Filtered videos:', filteredVideos);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mandi Videos</h1>
          <p className="text-gray-600 mt-1">Manage your mandi videos and content</p>
        </div>
        <button
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          onClick={() => setShowAddForm((v) => !v)}
        >
          <FiPlus className="mr-2 h-4 w-4" />
          Add New Video
        </button>
      </div>

      {/* Add Video Form */}
      {showAddForm && (
        <form onSubmit={handleAddVideo} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1">Title</label>
              <input name="title" value={addForm.title} onChange={handleAddFormChange} className="w-full border rounded px-3 py-2" required />
              {errors.title && <div className="text-red-600 text-xs mt-1">{errors.title}</div>}
            </div>
            <div>
              <label className="block font-medium mb-1">Title (Hindi)</label>
              <input name="title_hindi" value={addForm.title_hindi} onChange={handleAddFormChange} className="w-full border rounded px-3 py-2" />
              {errors.title_hindi && <div className="text-red-600 text-xs mt-1">{errors.title_hindi}</div>}
            </div>
            <div>
              <label className="block font-medium mb-1">Category</label>
              <input name="category" value={addForm.category} onChange={handleAddFormChange} className="w-full border rounded px-3 py-2" />
              {errors.category && <div className="text-red-600 text-xs mt-1">{errors.category}</div>}
            </div>
            <div>
              <label className="block font-medium mb-1">Crop <span className="text-gray-500 font-normal">(Optional)</span></label>
              <select name="crop_id" value={addForm.crop_id} onChange={handleAddFormChange} className="w-full border rounded px-3 py-2">
                <option value="">Select Crop (Optional)</option>
                {crops.map((crop) => (
                  <option key={crop.id} value={crop.id}>{crop.name} {crop.name_hindi ? `(${crop.name_hindi})` : ''}</option>
                ))}
                <option value="other">Other (अन्य)</option>
              </select>
              {errors.crop_id && <div className="text-red-600 text-xs mt-1">{errors.crop_id}</div>}
            </div>
          
            <div>
  <label className="block font-medium mb-1">Date</label>
  <input
    name="date"
    type="date"
    value={addForm.date}
    onChange={handleAddFormChange}
    className="w-full border rounded px-3 py-2"
  />
  {addForm.date && (
    <div className="mt-1 text-green-700 text-sm font-semibold">
      {new Date(addForm.date).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
    </div>
  )}
</div>
            <div>
              <label className="block font-medium mb-1">Likes</label>
              <input 
                name="likes" 
                type="number"
                value={addForm.likes} 
                onChange={handleAddFormChange} 
                className="w-full border rounded px-3 py-2" 
                placeholder="e.g., 100"
                min="0"
              />
              {errors.likes && <div className="text-red-600 text-xs mt-1">{errors.likes}</div>}
            </div>
           
          </div>
          <div>
            <label className="block font-medium mb-1">Description</label>
            <textarea name="description" value={addForm.description} onChange={handleAddFormChange} className="w-full border rounded px-3 py-2" />
            {errors.description && <div className="text-red-600 text-xs mt-1">{errors.description}</div>}
          </div>
          <div>
            <label className="block font-medium mb-1">Description (Hindi)</label>
            <textarea name="description_hindi" value={addForm.description_hindi} onChange={handleAddFormChange} className="w-full border rounded px-3 py-2" />
            {errors.description_hindi && <div className="text-red-600 text-xs mt-1">{errors.description_hindi}</div>}
          </div>
          <div>
            <label className="block font-medium mb-1">Video File</label>
            <input type="file" accept="video/*" onChange={e => setVideoFile(e.target.files?.[0] || null)} className="w-full border rounded px-3 py-2" required disabled={uploading} />
            {errors.video && <div className="text-red-600 text-xs mt-1">{errors.video}</div>}
            {videoFile && (
              <div className="mt-2 text-sm text-gray-600">
                <p><strong>Selected file:</strong> {videoFile.name}</p>
                <p><strong>Size:</strong> {(videoFile.size / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
            )}
          </div>

          {/* Upload Progress Bar */}
          {uploading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">वीडियो अपलोड हो रहा है... (Uploading Video...)</span>
                <span className="text-sm font-semibold text-blue-900">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-300 ease-out relative overflow-hidden"
                  style={{ width: `${uploadProgress}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
                </div>
              </div>
              <div className="mt-2 text-xs text-blue-700 text-center">
                {uploadProgress < 100 ? 'कृपया प्रतीक्षा करें... (Please wait...)' : 'अपलोड पूरा हो रहा है... (Finalizing upload...)'}
              </div>
            </div>
          )}
       
          <div>
            <button type="submit" className={`px-6 py-2 rounded transition-colors ${
              uploading 
                ? 'bg-gray-400 text-white cursor-not-allowed' 
                : 'bg-green-600 text-white hover:bg-green-700'
            }`} disabled={uploading}>
              {uploading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  अपलोड हो रहा है... ({uploadProgress}%)
                </div>
              ) : (
                'Upload Video'
              )}
            </button>
            <button 
              type="button" 
              className="ml-3 px-6 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors" 
              onClick={() => setShowAddForm(false)}
              disabled={uploading}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiPlay className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Videos</p>
              <p className="text-xl font-semibold text-gray-900">{videos.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Videos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVideos.map((video) => (
          <div key={video.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="relative">
              <video
                controls
                src={video.thumbnail}
                className="w-full h-40 object-cover"
                poster=""
              >
                Your browser does not support the video tag.
              </video>
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                {video.duration}
              </div>
              <div className="absolute top-2 left-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  video.status === 'published' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {video.status}
                </span>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{video.title}</h3>
              <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                <span>{video.category}</span>
                <span>{video.views} views</span>
              </div>
              <p className="text-sm text-gray-500 mb-4">Uploaded: {video.uploadDate}</p>
              <div className="flex items-center justify-between">
               
                <div className="flex items-center space-x-2">
                 
                  <button
                    className="p-1 text-gray-400 hover:text-red-600"
                    onClick={() => handleDeleteVideo(video.id)}
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredVideos.length === 0 && (
        <div className="text-center py-12">
          <FiPlay className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No videos found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterStatus !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by uploading your first video.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default MandiBhavVideos;
